import startOfDay from 'date-fns/startOfDay';
import { groupBy, sortBy, values } from 'micro-dash';
import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingCalendarEventDetailInternalResponse } from 'api';
import { AvailableTime } from 'types/user';
import clsx from 'clsx';
import { format } from 'utils/date';
import styles from './styles.module.css';

interface Props {
  timeSlots: BookingCalendarEventDetailInternalResponse[];
  selectedTime?: BookingCalendarEventDetailInternalResponse;
  disabled: boolean;
  onSelect?: (time: BookingCalendarEventDetailInternalResponse) => void;
}

const ITEM_VISIBLE = 3;

const AvailableTimeSlots: React.FunctionComponent<Props> = ({ timeSlots, selectedTime, disabled, onSelect }) => {
  const { t } = useTranslation();

  const slotsByDay = sortBy(
    values(groupBy(timeSlots, (slot) => startOfDay(new Date(slot.startTime as string)).toString())).map(
      (slotsByDayItem): AvailableTime => {
        return {
          day: new Date(slotsByDayItem[0].startTime as string),
          slots: sortBy(slotsByDayItem, (slot) => new Date(slot.startTime as string)).map((slot) => ({
            display: format(new Date(slot.startTime as string), 'HH:mm'),
            value: slot,
          })),
        };
      }
    ),
    (group) => group.day
  );

  const [collapsed, toggleCollapsed] = useState(slotsByDay.length > ITEM_VISIBLE);

  const displayItems = useMemo(() => {
    if (collapsed) {
      return slotsByDay.filter((item, index) => index < ITEM_VISIBLE);
    }

    return slotsByDay;
  }, [collapsed, slotsByDay]);

  return (
    <div>
      <div className={clsx('text-white font-bold mb-4 wisory-title')}>
        {t('REACT_BOOKING_FRONTEND.booking2.selectTime.title')}
      </div>
      <div className="text-black mb-6 font-openSans">{t('REACT_BOOKING_FRONTEND.booking2.selectTime.subtitle')}</div>
      <div className={clsx('font-openSans', { [styles.disabledSlots]: disabled })}>
        {displayItems.map((timeSlot, index) => (
          <Fragment key={timeSlot.day.toString()}>
            <div className={styles.divider} />

            <div
              className={clsx('flex items-baseline pt-4 pb-4', {
                hidden: collapsed && index >= ITEM_VISIBLE,
              })}
            >
              <div className="w-3/12 text-black font-bold capitalize">{format(timeSlot.day, 'dd MMMM')}</div>
              <div className={clsx(styles.slotWrapper, 'w-9/12')}>
                {timeSlot.slots.map((slot) => (
                  <span
                    key={slot.value.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect && onSelect(slot.value)}
                    data-slot-id={slot.value.id}
                    className={clsx('cursor-pointer mr-3', styles.slot, {
                      [styles.selected]: selectedTime === slot.value,
                    })}
                  >
                    {slot.display}
                  </span>
                ))}
              </div>
            </div>
          </Fragment>
        ))}
      </div>

      {collapsed && (
        <div className="text-center md:text-left font-openSans">
          <div className={styles.divider} />
          <div
            className="text-sm text-white py-4 cursor-pointer"
            onClick={() => toggleCollapsed((visible) => !visible)}
          >
            {t('profile.link.expand')} ▾
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailableTimeSlots;
